import React from 'react';
import ResumeTemplate1 from 'assets/resumetemplates/resumetemplate1/ResumeTemplate1';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import ReactDOMServer from 'react-dom/server';
import { resumeTemplateList } from 'constants/resume-template-list.const';
import { createRoot } from 'react-dom/client'; // added

const useExportPDF = () => {

    const exportResume = async ({ data, templateName }) => {
        const template = resumeTemplateList.find(template => template.name == templateName);
        const content = document.createElement('div');
        content.innerHTML = ReactDOMServer.renderToStaticMarkup(React.cloneElement(template.template, { data }));

        // Append the hidden content to the document body (temporarily)
        document.body.appendChild(content);

        // Use html2canvas to capture the dynamically created content
        const canvas = await html2canvas(content, { scale: 2, allowTaint: true, useCORS: true }); // Increase the scale for better quality
        const contentWidth = canvas.width;
        const contentHeight = canvas.height;

        const pageHeight = 277; // A4 page height in mm
        const pageWidth = 210; // A4 page width in mm
        const scaleFactor = pageWidth / contentWidth; // Scale to fit the width of the page

        const pdf = new jsPDF('p', 'mm', [210, 287]); // Portrait mode, mm units, A4 paper size

        let yOffset = 0;
        let pageIndex = 0;

        const topMargin = template.margin_top; // Set top margin value (in mm)
        const bottomMargin = template.margin_bottom; // Set bottom margin value (in mm)
        const usablePageHeight = pageHeight - topMargin - bottomMargin; // Height available for content

        const sliceHeight = usablePageHeight / scaleFactor; // Height of each slice for content

        while (yOffset < contentHeight) {
            const canvasSlice = document.createElement('canvas');
            canvasSlice.width = contentWidth;
            canvasSlice.height = Math.min(contentHeight - yOffset, sliceHeight); // Adjust slice height

            const ctx = canvasSlice.getContext('2d');
            ctx.drawImage(canvas, 0, yOffset, contentWidth, canvasSlice.height, 0, 0, canvasSlice.width, canvasSlice.height);

            const imgData = canvasSlice.toDataURL('image/JPEG', 0.7);

            if (pageIndex > 0) pdf.addPage(); // Add a new page after the first
            pdf.addImage(imgData, 'PNG', 0, topMargin, pageWidth, canvasSlice.height * scaleFactor); // Add image to PDF with top margin

            yOffset += canvasSlice.height; // Increment yOffset by the full height of the canvas slice
            pageIndex++;
        }

        pdf.save('exported_template.pdf');

        document.body.removeChild(content);
    }

    const exportHtmlResume = async ({ data, templateName }) => {
        const template = resumeTemplateList.find(template => template.name == templateName);
        const content = document.createElement('div');
        content.innerHTML = ReactDOMServer.renderToStaticMarkup(React.cloneElement(template.template, { data }));

        // Append the hidden content to the document body (temporarily)
        document.body.appendChild(content);
        const html2pdf = (await import('html2pdf.js')).default;

        const a4Dimensions = [826, 1169];

        const opt = {
            margin: [template.margin_top, 4, template.margin_bottom, 4], // Define margins
            filename: 'resume.pdf',
            pagebreak: { avoid: '.avoid-break' },
            image: { type: 'jpeg', quality: 1 },
            html2canvas: { scale: 3, allowTaint: true, useCORS: true },
            jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait', compress: false }
        };

        await html2pdf().from(content).set(opt).save();

        document.body.removeChild(content);
    }   


     const exportComponentPdf = async ({
        element,                // React element to render (e.g. <Radar .../>)
        filename = 'component.pdf',
        waitForSelector = 'canvas', // CSS selector to wait for (default: chart canvas)
        waitTimeout = 2000,
        scale = 2,              // html2canvas scale for quality
        marginTop = 0,         // mm
        marginBottom = 0       // mm
    }) => {
        const container = document.createElement('div');
        container.style.position = 'fixed';
        container.style.left = '-10000px';
        container.style.top = '0';
        document.body.appendChild(container);

        // mount the React element so chart (canvas) can render
        const root = createRoot(container);
        root.render(element);

        // wait helper - resolves when selector exists or timeout
        const waitFor = (selector, timeout) => new Promise(resolve => {
            const interval = 100;
            let elapsed = 0;
            const t = setInterval(() => {
                if (container.querySelector(selector)) {
                    clearInterval(t);
                    resolve(true);
                }
                elapsed += interval;
                if (elapsed >= timeout) {
                    clearInterval(t);
                    resolve(false);
                }
            }, interval);
        });

        await waitFor(waitForSelector, waitTimeout);

        // capture
        const canvas = await html2canvas(container, { scale, allowTaint: true, useCORS: true });
        const contentWidth = canvas.width;
        const contentHeight = canvas.height;

        const pageHeight = 1120; // A4 height in mm
        const pageWidth = 899;  // A4 width in mm
        const scaleFactor = pageWidth / contentWidth;

        const pdf = new jsPDF('p', 'px', [899, 1120], true);

        let yOffset = 0;
        let pageIndex = 0;

        const usablePageHeight = pageHeight - marginTop - marginBottom;
        const sliceHeight = usablePageHeight / scaleFactor;

        while (yOffset < contentHeight) {
            const canvasSlice = document.createElement('canvas');
            canvasSlice.width = contentWidth;
            canvasSlice.height = Math.min(contentHeight - yOffset, sliceHeight);

            const ctx = canvasSlice.getContext('2d');
            ctx.drawImage(canvas, 0, yOffset, contentWidth, canvasSlice.height, 0, 0, canvasSlice.width, canvasSlice.height);

            const imgData = canvasSlice.toDataURL('image/JPEG', 2);

            if (pageIndex > 0) pdf.addPage();
            pdf.addImage(imgData, 'PNG', 0, marginTop, pageWidth, canvasSlice.height * scaleFactor);

            yOffset += canvasSlice.height;
            pageIndex++;
        }

        pdf.save(filename);

        // cleanup
        root.unmount();
        document.body.removeChild(container);
    };

    return { exportResume, exportHtmlResume, exportComponentPdf };
};

export default useExportPDF;